import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import LineChart from "../../components/LineChart";
import StatBox from "../../components/StatBox";
import { useEffect, useState, useRef } from "react";
import { useMediaQuery } from "@mui/material";

const GoldPriceSystem = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobileOrTablet = useMediaQuery("(max-width:1024px)");

  const [goldPrices, setGoldPrices] = useState({
    "24K": { current: 0, previous: 0 },
    "22K": { current: 0, previous: 0 },
    "21K": { current: 0, previous: 0 },
    "18K": { current: 0, previous: 0 },
  });

  const [chartData, setChartData] = useState([]);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [countdown, setCountdown] = useState(15);

  const sendPricesToAPI = async (gold24, silverKG) => {
    try {
      const response = await fetch("https://CrystalCodekw.com/API2.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          gold24: gold24,
          silverKg: silverKG,
        }),
      });

      if (!response.ok) throw new Error("❌ Failed to update DB");
      const result = await response.json();
      console.log("✅ DB Updated:", result);
    } catch (err) {
      console.error("❌ Error sending prices to API:", err);
    }
  };
  const fetchGoldPrices = async () => {
    try {
      const response = await fetch(
        "https://api.daralsabaek.com/api/goldAndFundBalance/getMetalPrices"
      );
      if (!response.ok) throw new Error("Failed to fetch gold prices");

      const data = await response.json();
      const gold24 = data?.result?.find((item) => item.metalType === 1);

      if (gold24 && gold24.buyPrice24 > 0) {
        const newPrice = parseFloat(gold24.buyPrice24);

        setGoldPrices((prev) => ({
          "24K": { current: newPrice, previous: prev["24K"].current },
          "22K": {
            current: newPrice * (22 / 24),
            previous: prev["22K"].current,
          },
          "21K": {
            current: newPrice * (21 / 24),
            previous: prev["21K"].current,
          },
          "18K": {
            current: newPrice * (18 / 24),
            previous: prev["18K"].current,
          },
        }));
        await sendPricesToAPI(newPrice, 0);
      }
    } catch (err) {
      console.error("❌ Error fetching gold prices:", err);
    }
  };

  const fetchChartData = async (period) => {
    const urls = {
      0: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=0",
      1: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=1",
      2: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=2",
      3: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=3",
      4: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=4",
      5: "https://api.daralsabaek.com/api/Chart/GetMetalPricesChartData?metalSymbol=1&periodType=5",
    };

    try {
      const response = await fetch(urls[period]);
      if (!response.ok) throw new Error("Failed to fetch chart data");

      const data = await response.json();
      if (data.rangeOfXaxis && data.rangeOfYaxis) {
        const formatted = [
          {
            id: "عيار 24",
            data: data.rangeOfXaxis.map((x, i) => {
              const d = new Date(x);
              d.setHours(d.getHours() + 3);
              return {
                x: d.toLocaleTimeString(),
                y: parseFloat(data.rangeOfYaxis[i].toFixed(3)),
              };
            }),
          },
        ];
        setChartData(formatted);
        const yVals = data.rangeOfYaxis.map((v) => parseFloat(v));
        setMinValue(Math.min(...yVals));
        setMaxValue(Math.max(...yVals));
      }
    } catch (err) {
      console.error("❌ Error fetching chart data:", err);
    }
  };

  useEffect(() => {
    fetchGoldPrices();
    fetchChartData(selectedPeriod);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchGoldPrices();
          fetchChartData(selectedPeriod);
          return 15;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedPeriod]);

  const elementToHideRef = useRef(null);
  const handleIframeLoad = () => {
    if (elementToHideRef.current) {
      elementToHideRef.current.style.display = "none";
    }
  };

  return (
    <Box m="20px" mt="-5px">
      <Box display="flex" flexDirection="column" alignItems="center">
        <img
          src={
            isMobileOrTablet
              ? "../../assets/logo22.png"
              : "../../assets/logo2.png"
          }
          alt="Logo"
          width={isMobileOrTablet ? "250px" : "auto"}
        />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns={isMobileOrTablet ? "1fr" : "repeat(12, 1fr)"}
        gap="20px"
        mt="15px"
      >
        {/* Chart Section */}
        <Box
          gridColumn={isMobileOrTablet ? "span 1" : "span 9"}
          backgroundColor={colors.primary[400]}
          p="20px"
        >
          <Typography
            variant="h1"
            color={colors.grey[100]}
            fontSize={isMobileOrTablet ? "18px" : "24px"}
            textAlign="center"
            mb="10px"
          >
            سيتم تحديث الأسعار خلال {countdown} ثانية حسب السعر العالمي
          </Typography>
          <Box height="400px">
            <LineChart
              data={chartData}
              minValue={minValue}
              maxValue={maxValue}
            />
          </Box>
        </Box>

        {/* Side Section */}
        <Box
          gridColumn={isMobileOrTablet ? "span 1" : "span 3"}
          display="flex"
          flexDirection="column"
          gap="20px"
        >
          {/* TradingView Widget */}
          <Box
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p="20px"
            borderRadius="10px"
          >
            <iframe
              src="https://www.tradingview-widget.com/embed-widget/single-quote/?locale=en#%7B%22frameElementId%22%3A%22scaled-frame%22%2C%22symbol%22%3A%22TVC%3AGOLD%22%2C%22width%22%3A370%2C%22colorTheme%22%3A%22dark%22%2C%22isTransparent%22%3Atrue%2C%22height%22%3A126%2C%22utm_source%22%3A%22daralsabaek.com%22%2C%22utm_medium%22%3A%22widget%22%2C%22utm_campaign%22%3A%22single-quote%22%2C%22page-uri%22%3A%22daralsabaek.com%2Ftrading-gold.html%22%7D"
              width="100%"
              height="100px"
              style={{ border: "none" }}
              onLoad={handleIframeLoad}
              title="TradingView Widget"
            />
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={isMobileOrTablet ? "repeat(2, 1fr)" : "1fr"}
            gap="15px"
          >
            {["24K", "22K", "21K", "18K"].map((karat) => (
              <Box
                key={karat}
                backgroundColor={colors.primary[400]}
                p="15px"
                borderRadius="8px"
                boxShadow="0 4px 8px rgba(0,0,0,0.1)"
                display="flex"
                justifyContent="center"
              >
                <StatBox
                  title={`${goldPrices[karat].current.toFixed(3)} KD`}
                  subtitle={`عيار ${karat}`}
                  priceData={goldPrices[karat]}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GoldPriceSystem;
